import React, {
  memo, useCallback, useMemo, useState,
} from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import Tooltip from '../Tooltip';
import ColorsHelper from '../../helpers/colorsHelper';
import Logger from '../../services/Logger';
import { changeColor } from '../../store/actions/assets';
import makeTooltipText from './helpers/tooltipColorText';

const colors = ColorsHelper.colors.list;

function Colors({
  color, assetId, disabled, colorAt, colorBy,
}) {
  const dispatch = useDispatch();
  const customLabels = useSelector((state) => state.user?.team?.settings?.colorLabels || {});
  const [showDrop, setShowDrop] = useState(false);

  const handleClick = useCallback(
    () => {
      const loggerText = showDrop ? 'Close' : 'Open';
      Logger.log('User', `ThumbnailColors${loggerText}`);
      if (!disabled) setShowDrop(!showDrop);
    },
    [showDrop, disabled],
  );

  const handleChange = useCallback(
    (event) => {
      const value = event.currentTarget.dataset.color || 'nocolor';
      if (!disabled) dispatch(changeColor([assetId], value));
      setShowDrop(false);
    },
    [dispatch, assetId, disabled],
  );

  const tooltipText = useMemo(
    () => {
      const text = customLabels[color] || colors.find((colorItem) => colorItem.value === color)?.title || color;
      return makeTooltipText(text, colorAt, colorBy);
    },
    [color, customLabels, colorAt, colorBy],
  );

  const { text, ...buttonStyles } = useMemo(
    () => ({
      text: color === 'nocolor' ? 'color' : color,
      background: colors.find((colorItem) => colorItem.value === color)?.background || 'none',
      color: ['yellow', 'green'].includes(color) ? '#000' : '#fff',
    }),
    [color],
  );

  return (
    <div className="catalogItem__colors">
      <Tooltip content={tooltipText} placement="left">
        <span
          className={cn('catalogItem__colors-opener', { noColor: color === 'nocolor' })}
          role="button"
          tabIndex={0}
          onClick={handleClick}
          onKeyPress={handleClick}
          style={buttonStyles}
        >
          <span className="catalogItem__colors-text">
            {customLabels[color] || text}
          </span>
        </span>
      </Tooltip>
      <If condition={showDrop}>
        <div className="catalogItem__colors-list">
          {colors.map((colorItem) => (
            <div
              className={cn('catalogItem__colors-color', { act: colorItem.value === color })}
              key={colorItem.value}
              tabIndex={0}
              role="button"
              style={{ background: colorItem.background }}
              data-color={colorItem.value}
              onClick={handleChange}
              onKeyPress={handleChange}
            >
              <If condition={colorItem.value === 'nocolor'}>none</If>
            </div>
          ))}
        </div>
      </If>
    </div>
  );
}

Colors.propTypes = {
  assetId: PropTypes.string.isRequired,
  color: PropTypes.string.isRequired,
  disabled: PropTypes.bool.isRequired,
  colorAt: PropTypes.string,
  colorBy: PropTypes.shape({
    _id: PropTypes.string,
    displayName: PropTypes.string,
    email: PropTypes.string,
  }),
};

export default memo(Colors);
